import { z } from 'zod';
import type { ProjectionItemInput, ProjectionSummary } from './cashflow.ts';
import { addDays } from './facilities-preventive.ts';
import { fromScaled, toScaled } from './money.ts';
import { decimalString } from './schemas.ts';
import { addMonthsClamped } from './treasury-forecast.ts';

/**
 * Cenários de caixa (ADR 0084, Entrega 8; §11 da especificação). Um cenário muda a projeção oficial
 * só na tela: reduz entradas, aumenta saídas, atrasa ou antecipa, aplica choque de câmbio e soma
 * eventos (investimentos, contratações, distribuição de lucros, aportes, empréstimos...). **Nunca
 * gera lançamento real.** Contratações e desligamentos entram somados como "Pessoal" (D-04).
 */

export const SCENARIO_KINDS = ['base', 'conservative', 'optimistic', 'custom'] as const;
export const SCENARIO_EVENT_KINDS = [
  'investment',
  'hiring',
  'termination',
  'profit_distribution',
  'intercompany_transfer',
  'capital_contribution',
  'loan',
  'loan_repayment',
  'extraordinary_expense',
  'other',
] as const;
export type ScenarioEventKind = (typeof SCENARIO_EVENT_KINDS)[number];

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');
const pct = (min: number, max: number) => z.number().min(min).max(max).default(0);

export const scenarioEvent = z.object({
  kind: z.enum(SCENARIO_EVENT_KINDS),
  label: z.string().trim().min(2).max(200),
  direction: z.enum(['in', 'out']),
  date: isoDate,
  amount: decimalString(15, 4).refine((v) => toScaled(v) > 0n, 'Informe um valor maior que zero'),
  currency: z.string().regex(/^[A-Z]{3}$/),
  /** Repete todo mês, a partir da data (1 = uma vez). */
  months: z.number().int().min(1).max(60).default(1),
  accountKey: z.uuid().nullable().optional(),
});
export type ScenarioEvent = z.infer<typeof scenarioEvent>;

export const scenarioVariables = z.object({
  /** Redução das entradas previstas, em %. */
  receiptsReductionPct: pct(0, 100),
  /** Crescimento das saídas previstas, em % (negativo = corte). */
  expenseGrowthPct: pct(-100, 300),
  /** Atraso (positivo) ou antecipação (negativo) das entradas, em dias. */
  receiptDelayDays: z.number().int().min(-60).max(180).default(0),
  /** Atraso (positivo) ou antecipação (negativo) das saídas, em dias. */
  paymentShiftDays: z.number().int().min(-60).max(180).default(0),
  /** Choque de câmbio, em %, sobre os valores em moeda diferente da moeda da empresa. */
  fxShockPct: pct(-90, 300),
  events: z.array(scenarioEvent).max(200).default([]),
});
export type ScenarioVariables = z.infer<typeof scenarioVariables>;

export const scenarioInput = z.object({
  legalEntityId: z.uuid(),
  name: z.string().trim().min(2).max(120),
  kind: z.enum(SCENARIO_KINDS),
  variables: scenarioVariables,
  horizonDays: z.number().int().min(7).max(365).default(90),
  notes: z
    .string()
    .trim()
    .max(2000)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
});
export type ScenarioInput = z.infer<typeof scenarioInput>;

/** Valores prontos dos cenários padrão (a pessoa ajusta depois). */
export const SCENARIO_PRESETS: Record<
  'base' | 'conservative' | 'optimistic',
  Partial<ScenarioVariables>
> = {
  base: {},
  conservative: { receiptsReductionPct: 10, expenseGrowthPct: 5, receiptDelayDays: 15 },
  optimistic: { receiptsReductionPct: 0, expenseGrowthPct: -5, receiptDelayDays: -5 },
};

/** Multiplica um valor (string decimal) por (100 + pct) / 100, arredondando para 4 casas. */
function scale(amount: string, pctChange: number): string {
  if (pctChange === 0) return amount;
  const bp = BigInt(Math.round((100 + pctChange) * 100));
  const v = toScaled(amount) * bp;
  const q = v / 10000n;
  const r = v % 10000n;
  const half = r * 2n >= 10000n || r * 2n <= -10000n;
  return fromScaled(half ? q + (v < 0n ? -1n : 1n) : q);
}

/**
 * Aplica o cenário aos itens da projeção oficial (sem mudar nada gravado) e soma os eventos.
 * `functionalCurrency` é a moeda da empresa: o choque de câmbio vale para as outras.
 */
export function applyScenario(
  items: readonly ProjectionItemInput[],
  v: ScenarioVariables,
  functionalCurrency: string,
): ProjectionItemInput[] {
  const out = items.map((it) => {
    const inflow = !it.amount.startsWith('-');
    let amount = it.amount;
    if (inflow) amount = scale(amount, -v.receiptsReductionPct);
    else amount = scale(amount, v.expenseGrowthPct);
    if (it.currency !== functionalCurrency) amount = scale(amount, v.fxShockPct);
    const shift = inflow ? v.receiptDelayDays : v.paymentShiftDays;
    return { ...it, amount, date: shift ? addDays(it.date, shift) : it.date };
  });
  v.events.forEach((e, i) => {
    for (let m = 0; m < e.months; m++) {
      const payroll = e.kind === 'hiring' || e.kind === 'termination';
      out.push({
        sourceType: 'treasury.scenario',
        sourceId: `${i}`,
        accountKey: e.accountKey ?? null,
        currency: e.currency,
        date: addMonthsClamped(e.date, m),
        amount:
          e.direction === 'in' ? fromScaled(toScaled(e.amount)) : fromScaled(-toScaled(e.amount)),
        layer: 'scenario',
        basis: 'forecast',
        label: payroll ? 'Pessoal (cenário)' : e.label,
      });
    }
  });
  return out.filter((it) => toScaled(it.amount) !== 0n);
}

export interface ScenarioResult {
  currency: string;
  lowest: { date: string; closing: string };
  firstNegative: string | null;
  /** Quanto falta no pior dia (0 quando nunca fica negativo). */
  maxFundingNeed: string;
  /** Diferença do saldo mais baixo em relação à Base (cenário − base). */
  lowestVsBase: string | null;
}

/** Resultados do cenário a partir dos resumos da projeção (e da Base, para a diferença). */
export function scenarioResults(
  summaries: readonly ProjectionSummary[],
  base: readonly ProjectionSummary[] | null,
): ScenarioResult[] {
  return summaries.map((s) => {
    const low = toScaled(s.lowest.closing);
    const b = base?.find((x) => x.currency === s.currency);
    return {
      currency: s.currency,
      lowest: s.lowest,
      firstNegative: s.firstNegative,
      maxFundingNeed: fromScaled(low < 0n ? -low : 0n),
      lowestVsBase: b ? fromScaled(low - toScaled(b.lowest.closing)) : null,
    };
  });
}
